import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Gift, Mail, Phone, User, MessageSquare, ChevronRight, ChevronLeft,
  Check, CreditCard, Loader2, AlertCircle, Calendar, ExternalLink,
  Lock, ShieldCheck, Store, Copy, Share2, Search, Wallet
} from 'lucide-react';
import { copyText } from '@/lib/copyText';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_COLOR = '#f59e0b';

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)].join(',');
}

export default function PublicGiftCardPage() {
  const { slug } = useParams();
  const [settings, setSettings] = useState(null);
  const [store, setStore] = useState(null);
  const [step, setStep] = useState('amount');
  const [amount, setAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({
    recipient_name: '', recipient_email: '', recipient_phone: '',
    purchaser_name: '', purchaser_phone: '',
    sender_message: '', send_now: true, send_at: '',
  });
  // The sale on the server: { sale_id, access_token, status, payment: { pay_url, status } ... }
  const [sale, setSale] = useState(null);
  const [processing, setProcessing] = useState(false);
  // One idempotency key per checkout: a retried click finds the same sale
  const checkoutKey = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/giftcards/public/settings/${slug}`);
        if (r.ok) setSettings(await r.json());
        else setSettings({ enabled: false });
      } catch { setSettings({ enabled: false }); }
      try {
        const r = await fetch(`${API}/api/store/${slug}`);
        if (r.ok) setStore(await r.json());
      } catch {}
    })();
  }, [slug]);

  const brandColor = settings?.brand_color || DEFAULT_COLOR;
  const heroUrl = settings?.storefront_hero_url;
  const logoUrl = settings?.storefront_logo_url;
  const tagline = settings?.storefront_tagline;
  const rgb = hexToRgb(brandColor);

  const fixedAmounts = (() => {
    if (settings?.fixed_amounts_json) {
      try {
        const arr = typeof settings.fixed_amounts_json === 'string'
          ? JSON.parse(settings.fixed_amounts_json) : settings.fixed_amounts_json;
        if (Array.isArray(arr) && arr.length > 0) return arr;
      } catch {}
    }
    return [25, 50, 100, 200];
  })();

  const finalAmount = amount === 'custom' ? parseFloat(customAmount) : amount;
  const hasContact = form.recipient_email || form.recipient_phone;
  const canProceed = finalAmount > 0 && form.recipient_name && hasContact;
  const customValid = (() => {
    if (amount !== 'custom') return true;
    const v = parseFloat(customAmount);
    if (!v || v <= 0) return false;
    if (settings?.custom_amount_min && v < settings.custom_amount_min) return false;
    if (settings?.custom_amount_max && v > settings.custom_amount_max) return false;
    return true;
  })();

  const STEPS = ['amount', 'details', 'confirm', 'payment'];
  const stepIndex = (s) => STEPS.indexOf(s);

  const newCheckoutKey = () => `sf-${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const readJson = async (r) => {
    const text = await r.text();
    try { return JSON.parse(text); } catch { throw new Error(`Server error (${r.status}). Please try again later.`); }
  };

  const showSale = useCallback((data) => {
    setSale(data);
    if (data.issued) {
      setResult({
        amount: data.amount_cents / 100,
        code_last4: data.gift_card?.code_last4,
        delivery_status: data.gift_card?.delivery_status,
        hosted_page_url: data.gift_card?.hosted_page_url,
      });
      setStep('success');
    }
  }, []);

  // Start checkout: the server creates the purchase and the secure payment page.
  // Card details are entered on that page only, never here.
  const handlePay = async () => {
    setProcessing(true);
    setError('');
    try {
      if (!checkoutKey.current) checkoutKey.current = newCheckoutKey();
      const payload = {
        store_slug: slug,
        amount_cents: Math.round(finalAmount * 100),
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email || undefined,
        recipient_phone: form.recipient_phone || undefined,
        purchaser_name: form.purchaser_name || undefined,
        purchaser_phone: form.purchaser_phone || undefined,
        sender_message: form.sender_message || undefined,
        send_at: !form.send_now && form.send_at ? new Date(form.send_at).toISOString() : undefined,
        idempotency_key: checkoutKey.current,
      };
      const r = await fetch(`${API}/api/storefront/gift-card/purchase`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await readJson(r);
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not start the payment');
      showSale(data);
    } catch (e) {
      setError(e.message || 'Could not start the payment. Please try again.');
    }
    setProcessing(false);
  };

  const payAgain = async () => {
    if (!sale) return;
    setProcessing(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/storefront/gift-card/sales/${sale.sale_id}/pay-again?t=${encodeURIComponent(sale.access_token)}`, { method: 'POST' });
      const data = await readJson(r);
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not start the payment');
      showSale(data);
    } catch (e) {
      setError(e.message || 'Could not start the payment. Please try again.');
    }
    setProcessing(false);
  };

  // While the buyer pays on the secure page, ask the server for the verdict.
  // The gift card exists only once the payment is approved.
  useEffect(() => {
    if (!sale || sale.issued || sale.status === 'review') return undefined;
    const declined = sale.status === 'awaiting_payment' && sale.payment?.is_final;
    if (declined) return undefined;
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/storefront/gift-card/sales/${sale.sale_id}?t=${encodeURIComponent(sale.access_token)}`);
        if (r.ok) showSale(await r.json());
        else setSale({ ...sale });
      } catch {
        setSale({ ...sale });
      }
    }, sale.paid ? 5000 : 3000);
    return () => clearTimeout(timer);
  }, [sale, showSale]);

  const reset = () => {
    setStep('amount'); setAmount(null); setCustomAmount('');
    setForm({ recipient_name: '', recipient_email: '', recipient_phone: '',
      purchaser_name: '', purchaser_phone: '', sender_message: '', send_now: true, send_at: '' });
    setSale(null); checkoutKey.current = null;
    setResult(null); setError('');
  };

  // Loading
  if (!settings) return (
    <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
        <p className="text-gray-500 text-sm">Loading gift cards...</p>
      </div>
    </div>
  );

  // Disabled
  if (!settings.enabled || !settings.allow_virtual) return (
    <div className="min-h-screen bg-[#0a0a14] flex items-center justify-center px-4">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-800/50 flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-gray-600" />
        </div>
        <h1 className="text-white text-xl font-semibold mb-2">Gift Cards Unavailable</h1>
        <p className="text-gray-500 text-sm max-w-xs mx-auto">Gift cards are not currently available for this restaurant.</p>
      </div>
    </div>
  );

  const storeName = store?.restaurant?.name || slug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Restaurant';
  const accentBg = `rgba(${rgb}, 1)`;
  const accentBgLight = `rgba(${rgb}, 0.15)`;
  const accentBorder = `rgba(${rgb}, 0.5)`;
  const accentText = brandColor;
  const accentShadow = `0 8px 24px rgba(${rgb}, 0.15)`;

  return (
    <div className="min-h-screen bg-[#0a0a14]" data-testid="gc-storefront">
      {heroUrl && (
        <div className="relative h-44 sm:h-56 overflow-hidden" data-testid="gc-hero-banner">
          <img src={heroUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[#0a0a14]" />
        </div>
      )}
      <header className={`${heroUrl ? '-mt-14 relative z-10' : ''} bg-[#0f0f1e]/90 border-b border-white/[0.04] backdrop-blur-xl`}>
        <div className="max-w-lg mx-auto px-4 py-3.5 flex items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="w-11 h-11 rounded-xl object-cover border border-white/10 shadow-lg" data-testid="gc-logo" />
          ) : (
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: accentBgLight }}>
              <Store className="w-5 h-5" style={{ color: accentText }} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-white font-semibold text-base leading-tight truncate">{storeName}</h1>
            <p className="text-gray-500 text-xs flex items-center gap-1">
              {tagline || <><Gift className="w-3 h-3" /> Gift Cards</>}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 pb-20">
        {step !== 'success' && (
          <div className="flex items-center gap-1.5 mb-7" data-testid="gc-progress">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all duration-300"
                  style={step === s ? { background: accentBg, color: '#000', transform: 'scale(1.1)', boxShadow: accentShadow }
                    : stepIndex(step) > i ? { background: accentBgLight, color: accentText }
                    : { background: 'rgba(255,255,255,0.03)', color: '#555' }}>
                  {stepIndex(step) > i ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-[2px] rounded-full transition-colors duration-300"
                    style={{ background: stepIndex(step) > i ? `rgba(${rgb}, 0.3)` : 'rgba(255,255,255,0.03)' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* STEP 1: Amount */}
        {step === 'amount' && (
          <div className="space-y-5" data-testid="gc-step-amount">
            <div>
              <h2 className="text-white text-xl font-bold mb-1">Choose an Amount</h2>
              <p className="text-gray-500 text-sm">Select a gift card value for someone special</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {fixedAmounts.map(a => (
                <button key={a} onClick={() => { setAmount(a); setCustomAmount(''); }}
                  data-testid={`gc-amount-${a}`}
                  className="py-4 rounded-xl text-center font-bold text-lg transition-all duration-200 border"
                  style={amount === a
                    ? { background: accentBgLight, borderColor: accentBorder, color: accentText, boxShadow: `0 4px 12px rgba(${rgb}, 0.08)` }
                    : { background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)', color: '#fff' }}>
                  ${a}
                </button>
              ))}
            </div>
            {settings.custom_amount_enabled && (
              <div>
                <button onClick={() => setAmount('custom')} data-testid="gc-amount-custom-btn"
                  className="w-full py-3 rounded-xl text-center font-medium border transition-all duration-200"
                  style={amount === 'custom'
                    ? { background: accentBgLight, borderColor: accentBorder, color: accentText }
                    : { background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)', color: '#999' }}>
                  Custom Amount
                </button>
                {amount === 'custom' && (
                  <div className="mt-3 relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg font-medium">$</span>
                    <input type="number" autoFocus value={customAmount}
                      onChange={e => setCustomAmount(e.target.value)}
                      placeholder={settings.custom_amount_min && settings.custom_amount_max ? `${settings.custom_amount_min} - ${settings.custom_amount_max}` : 'Enter amount'}
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white text-lg outline-none transition-colors"
                      data-testid="gc-custom-input" />
                    {!customValid && customAmount && (
                      <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {settings.custom_amount_min && settings.custom_amount_max ? `Amount must be $${settings.custom_amount_min} - $${settings.custom_amount_max}` : 'Enter a valid amount'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            <button onClick={() => setStep('details')} disabled={!amount || (amount === 'custom' && !customValid)} data-testid="gc-next-details"
              className="w-full py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ background: accentBg, color: '#000' }}>
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* STEP 2: Details */}
        {step === 'details' && (
          <div className="space-y-5" data-testid="gc-step-details">
            <div>
              <h2 className="text-white text-xl font-bold mb-1">Gift Card Details</h2>
              <p className="text-gray-500 text-sm"><span className="font-semibold" style={{ color: accentText }}>${finalAmount}</span> gift card</p>
            </div>
            <div className="space-y-4">
              <SectionLabel>Recipient</SectionLabel>
              <div className="space-y-3">
                <InputField icon={User} placeholder="Recipient Name *" value={form.recipient_name} onChange={v => setForm(f => ({ ...f, recipient_name: v }))} testId="gc-sf-recip-name" />
                {settings.allow_email_delivery && <InputField icon={Mail} type="email" placeholder="Recipient Email" value={form.recipient_email} onChange={v => setForm(f => ({ ...f, recipient_email: v }))} testId="gc-sf-recip-email" />}
                {settings.allow_sms_delivery && <InputField icon={Phone} placeholder="Recipient Phone" value={form.recipient_phone} onChange={v => setForm(f => ({ ...f, recipient_phone: v }))} testId="gc-sf-recip-phone" />}
                {!hasContact && <p className="text-red-400/80 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Email or phone required for delivery</p>}
              </div>
              <SectionLabel>From (optional)</SectionLabel>
              <div className="space-y-3">
                <InputField icon={User} placeholder="Your Name" value={form.purchaser_name} onChange={v => setForm(f => ({ ...f, purchaser_name: v }))} testId="gc-sf-buyer-name" />
                <InputField icon={Phone} placeholder="Your Phone (for confirmation)" value={form.purchaser_phone} onChange={v => setForm(f => ({ ...f, purchaser_phone: v }))} testId="gc-sf-buyer-phone" />
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-600" />
                  <textarea placeholder="Add a personal message (optional)" value={form.sender_message}
                    onChange={e => setForm(f => ({ ...f, sender_message: e.target.value }))} rows={2}
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 outline-none resize-none text-sm transition-colors"
                    data-testid="gc-sf-message" />
                </div>
              </div>
              {settings.allow_scheduled_send && (
                <>
                  <SectionLabel>Delivery Schedule</SectionLabel>
                  <div className="flex gap-2">
                    <button onClick={() => setForm(f => ({ ...f, send_now: true, send_at: '' }))} data-testid="gc-send-now-btn"
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all duration-200"
                      style={form.send_now ? { background: accentBgLight, borderColor: accentBorder, color: accentText } : { background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)', color: '#999' }}>
                      Send Now
                    </button>
                    <button onClick={() => setForm(f => ({ ...f, send_now: false }))} data-testid="gc-schedule-btn"
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all duration-200 flex items-center justify-center gap-1.5"
                      style={!form.send_now ? { background: accentBgLight, borderColor: accentBorder, color: accentText } : { background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.06)', color: '#999' }}>
                      <Calendar className="w-3.5 h-3.5" /> Schedule
                    </button>
                  </div>
                  {!form.send_now && (
                    <input type="datetime-local" value={form.send_at} onChange={e => setForm(f => ({ ...f, send_at: e.target.value }))} data-testid="gc-schedule-input"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none text-sm transition-colors" />
                  )}
                </>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep('amount')} data-testid="gc-back-amount" className="px-5 py-3.5 rounded-xl font-medium text-gray-400 bg-white/[0.04] hover:bg-white/[0.08] transition-colors flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep('confirm')} disabled={!canProceed} data-testid="gc-next-confirm"
                className="flex-1 py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: accentBg, color: '#000' }}>
                Review Order <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Confirm */}
        {step === 'confirm' && (
          <div className="space-y-5" data-testid="gc-step-confirm">
            <div>
              <h2 className="text-white text-xl font-bold mb-1">Review Your Order</h2>
              <p className="text-gray-500 text-sm">Confirm the details before payment</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/[0.06]" style={{ background: `linear-gradient(135deg, rgba(${rgb},0.1), rgba(${rgb},0.03))` }}>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Gift Card Amount</span>
                  <span className="text-2xl font-bold" style={{ color: accentText }}>${finalAmount}</span>
                </div>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm">
                <SummaryRow label="To" value={form.recipient_name} />
                {form.recipient_email && <SummaryRow label="Email" value={form.recipient_email} />}
                {form.recipient_phone && <SummaryRow label="Phone" value={form.recipient_phone} />}
                {form.purchaser_name && <SummaryRow label="From" value={form.purchaser_name} />}
                {form.sender_message && <SummaryRow label="Message" value={`"${form.sender_message}"`} />}
                <SummaryRow label="Delivery" value={form.send_now ? 'Immediate' : `Scheduled: ${form.send_at ? new Date(form.send_at).toLocaleString() : 'Not set'}`} />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep('details')} data-testid="gc-back-details" className="px-5 py-3.5 rounded-xl font-medium text-gray-400 bg-white/[0.04] hover:bg-white/[0.08] transition-colors flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep('payment')} data-testid="gc-proceed-payment"
                className="flex-1 py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                style={{ background: accentBg, color: '#000' }}>
                <Lock className="w-4 h-4" /> Proceed to Pay
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Payment on the secure hosted payment page */}
        {step === 'payment' && (
          <div className="space-y-5" data-testid="gc-step-payment">
            <div>
              <h2 className="text-white text-xl font-bold mb-1">Payment</h2>
              <p className="text-gray-500 text-sm">Total: <span className="font-semibold" style={{ color: accentText }}>${finalAmount}</span></p>
            </div>

            {processing ? (
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8 text-center" data-testid="gc-payment-processing">
                <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: brandColor }} />
                <p className="text-white font-medium mb-1">Preparing your secure payment page...</p>
                <p className="text-gray-500 text-sm">Please do not close this page</p>
              </div>
            ) : !sale ? (
              <>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5 text-sm text-gray-400" data-testid="gc-payment-intro">
                  <div className="flex items-center gap-2 text-white font-medium mb-2">
                    <CreditCard className="w-4 h-4" style={{ color: accentText }} /> Pay by card
                  </div>
                  You will enter your card on a secure payment page. Your gift card is sent once the payment is approved.
                </div>
                {error && <ErrorBox message={error} />}
                <button onClick={handlePay} data-testid="gc-pay-btn"
                  className="w-full py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                  style={{ background: accentBg, color: '#000' }}>
                  <Lock className="w-4 h-4" /> Continue to secure payment
                </button>
                <button onClick={() => { setStep('confirm'); setError(''); }} data-testid="gc-back-confirm"
                  className="w-full py-3 rounded-xl font-medium text-gray-400 bg-white/[0.04] hover:bg-white/[0.08] transition-colors flex items-center justify-center gap-1">
                  <ChevronLeft className="w-4 h-4" /> Back to Review
                </button>
              </>
            ) : (
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 text-center space-y-4" data-testid="gc-payment-status">
                {sale.status === 'awaiting_payment' && !sale.payment?.is_final && (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: brandColor }} />
                    <p className="text-white font-medium">Waiting for your payment</p>
                    <p className="text-gray-500 text-sm">Complete the payment on the secure page. This page updates by itself.</p>
                    {sale.payment?.pay_url && (
                      <a href={sale.payment.pay_url} target="_blank" rel="noopener noreferrer" data-testid="gc-open-payment-page"
                        className="w-full py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                        style={{ background: accentBg, color: '#000' }}>
                        <Lock className="w-4 h-4" /> Open secure payment page <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </>
                )}
                {sale.status === 'awaiting_payment' && sale.payment?.is_final && (
                  <>
                    <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
                    <p className="text-white font-medium">{sale.message}</p>
                    <button onClick={payAgain} data-testid="gc-pay-again"
                      className="w-full py-3.5 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                      style={{ background: accentBg, color: '#000' }}>
                      <Lock className="w-4 h-4" /> Try the payment again
                    </button>
                  </>
                )}
                {(sale.status === 'ready_to_issue' || sale.status === 'review') && (
                  <>
                    {sale.status === 'ready_to_issue'
                      ? <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: brandColor }} />
                      : <AlertCircle className="w-8 h-8 text-amber-400 mx-auto" />}
                    <p className="text-white font-medium" data-testid="gc-sale-message">{sale.message}</p>
                    <p className="text-gray-500 text-xs">Purchase reference: GC-{sale.sale_id}</p>
                  </>
                )}
                {error && <ErrorBox message={error} />}
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-gray-600 text-xs">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Secured by Aster Gateway</span>
            </div>
          </div>
        )}

        {/* STEP 5: Success + Social Sharing */}
        {step === 'success' && result && (
          <div className="text-center space-y-6 py-6" data-testid="gc-step-success">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-400" />
              </div>
            </div>
            <div>
              <h2 className="text-white text-xl font-bold mb-2">Gift Card Sent!</h2>
              <p className="text-gray-400 text-sm max-w-xs mx-auto">
                {form.recipient_name || 'Recipient'} will receive their <span className="font-semibold" style={{ color: accentText }}>${result.amount || finalAmount}</span> gift card
                {form.recipient_email ? ' via email' : ' via SMS'}
              </p>
            </div>

            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl px-5 py-4 text-left space-y-2.5 text-sm">
              <SummaryRow label="Amount" value={`$${result.amount || finalAmount}`} />
              {form.recipient_name && <SummaryRow label="Recipient" value={form.recipient_name} />}
              {result.code_last4 && <SummaryRow label="Gift card" value={`ending in ${result.code_last4}`} />}
              {sale?.sale_id && <SummaryRow label="Purchase reference" value={`GC-${sale.sale_id}`} />}
              <SummaryRow label="Status" value={result.delivery_status === 'sent' ? 'Delivered' : 'Processing'} highlight />
            </div>

            {result.hosted_page_url && (
              <a href={result.hosted_page_url} target="_blank" rel="noopener noreferrer" data-testid="gc-view-link"
                className="inline-flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: accentText }}>
                <ExternalLink className="w-4 h-4" /> View Gift Card Page
              </a>
            )}

            {/* Social Sharing */}
            <ShareButtons storeName={storeName} amount={result.amount || finalAmount} slug={slug} brandColor={brandColor} rgb={rgb} />

            <button onClick={reset} data-testid="gc-buy-another"
              className="w-full py-3.5 rounded-xl font-semibold transition-all duration-200"
              style={{ background: accentBg, color: '#000' }}>
              Buy Another Gift Card
            </button>
          </div>
        )}

        {settings.terms_text && step !== 'success' && (
          <p className="text-gray-700 text-[10px] text-center mt-8 leading-relaxed">{settings.terms_text}</p>
        )}

        {/* Balance Checker Widget */}
        <BalanceChecker brandColor={brandColor} rgb={rgb} slug={slug} />
      </main>
    </div>
  );
}

/* ========== Error box ========== */

function ErrorBox({ message }) {
  return (
    <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-left" data-testid="gc-error">
      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
      <p className="text-red-400 text-sm">{message}</p>
    </div>
  );
}

/* ========== Balance Checker ========== */

function BalanceChecker({ brandColor, rgb, slug }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleCheck = async () => {
    if (!code.trim() || code.trim().length < 4) {
      setError('Please enter a valid gift card code');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const r = await fetch(`${API}/api/storefront/gift-card/check-balance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim(), store_slug: slug }),
      });
      let data;
      const text = await r.text();
      try { data = JSON.parse(text); } catch { throw new Error('Service unavailable'); }
      if (!r.ok) throw new Error(data.detail || 'Lookup failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Could not check balance');
    }
    setLoading(false);
  };

  const accentBg = `rgba(${rgb}, 1)`;

  return (
    <div className="mt-10 border-t border-white/[0.04] pt-6" data-testid="gc-balance-checker">
      <button
        onClick={() => { setOpen(!open); setError(''); setResult(null); }}
        className="w-full flex items-center justify-between text-left group"
        data-testid="gc-balance-toggle"
      >
        <span className="flex items-center gap-2 text-gray-400 text-sm font-medium group-hover:text-gray-300 transition-colors">
          <Wallet className="w-4 h-4" />
          Check Gift Card Balance
        </span>
        <ChevronRight className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
              <input
                type="text"
                placeholder="Enter gift card code"
                value={code}
                onChange={e => { setCode(e.target.value); setError(''); setResult(null); }}
                onKeyDown={e => e.key === 'Enter' && !loading && handleCheck()}
                data-testid="gc-balance-input"
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 outline-none text-sm transition-colors focus:border-white/20"
              />
            </div>
            <button
              onClick={handleCheck}
              disabled={loading || !code.trim()}
              data-testid="gc-balance-submit"
              className="px-5 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-40"
              style={{ background: accentBg, color: '#000' }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-xs" data-testid="gc-balance-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 space-y-2" data-testid="gc-balance-result">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-xs uppercase tracking-wider">Current Balance</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  result.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' :
                  result.status === 'redeemed' ? 'bg-gray-500/15 text-gray-400' :
                  'bg-amber-500/15 text-amber-400'
                }`}>{result.status}</span>
              </div>
              <p className="text-3xl font-bold text-white" data-testid="gc-balance-amount">
                ${(result.current_balance || 0).toFixed(2)}
              </p>
              <div className="text-gray-600 text-xs space-y-0.5">
                <p>Original value: ${(result.original_amount || 0).toFixed(2)}</p>
                {result.recipient_name && <p>Recipient: {result.recipient_name}</p>}
                {result.expires_at && <p>Expires: {new Date(result.expires_at).toLocaleDateString()}</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ========== Social Sharing ========== */

function SectionLabel({ children }) {
  return <p className="text-gray-500 text-[11px] uppercase tracking-wider font-medium pt-1">{children}</p>;
}

function InputField({ icon: Icon, placeholder, value, onChange, type = 'text', testId }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
      <input type={type} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-600 outline-none text-sm transition-colors focus:border-white/20" />
    </div>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-start gap-3">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-right ${highlight ? 'text-emerald-400 font-medium' : 'text-white'}`}>{value}</span>
    </div>
  );
}

function ShareButtons({ storeName, amount, slug, brandColor, rgb }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/store/${slug}/gift-cards`;
  const shareText = `I just sent a $${amount} gift card from ${storeName}! Get yours here:`;

  const copyLink = async () => {
    try {
      if (!(await copyText(shareUrl, null))) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${storeName} Gift Card`, text: shareText, url: shareUrl }); } catch {}
    }
  };

  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
  const smsBody = `${shareText} ${shareUrl}`;
  const smsUrl = `sms:?body=${encodeURIComponent(smsBody)}`;

  return (
    <div className="space-y-3" data-testid="gc-share-section">
      <p className="text-gray-500 text-xs uppercase tracking-wider font-medium">Share the love</p>
      <div className="flex items-center justify-center gap-2.5 flex-wrap">
        {/* Facebook */}
        <a href={fbUrl} target="_blank" rel="noopener noreferrer" data-testid="gc-share-facebook"
          className="w-10 h-10 rounded-xl bg-[#1877f2]/10 border border-[#1877f2]/20 flex items-center justify-center hover:bg-[#1877f2]/20 transition-colors"
          title="Share on Facebook">
          <svg className="w-4.5 h-4.5 text-[#1877f2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </a>
        {/* Instagram (copy) */}
        <button onClick={copyLink} data-testid="gc-share-instagram"
          className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#833ab4]/10 via-[#fd1d1d]/10 to-[#f77737]/10 border border-[#e1306c]/20 flex items-center justify-center hover:from-[#833ab4]/20 hover:via-[#fd1d1d]/20 hover:to-[#f77737]/20 transition-colors"
          title="Copy link for Instagram">
          <svg className="w-4.5 h-4.5 text-[#e1306c]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
        </button>
        {/* TikTok (copy) */}
        <button onClick={copyLink} data-testid="gc-share-tiktok"
          className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center hover:bg-white/[0.08] transition-colors"
          title="Copy link for TikTok">
          <svg className="w-4.5 h-4.5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.44v-7.1a8.16 8.16 0 005.58 2.19V11.3a4.85 4.85 0 01-2.58-.76v4.21"/></svg>
        </button>
        {/* SMS */}
        <a href={smsUrl} data-testid="gc-share-sms"
          className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center hover:bg-emerald-500/20 transition-colors"
          title="Share via SMS">
          <MessageSquare className="w-4 h-4 text-emerald-400" />
        </a>
        {/* Copy Link */}
        <button onClick={copyLink} data-testid="gc-share-copy"
          className="w-10 h-10 rounded-xl border flex items-center justify-center transition-colors"
          style={copied ? { background: `rgba(${rgb}, 0.15)`, borderColor: `rgba(${rgb}, 0.4)` } : { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.06)' }}
          title={copied ? 'Copied!' : 'Copy link'}>
          {copied ? <Check className="w-4 h-4" style={{ color: brandColor }} /> : <Copy className="w-4 h-4 text-gray-400" />}
        </button>
        {/* Native Share (mobile) */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button onClick={shareNative} data-testid="gc-share-native"
            className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center hover:bg-white/[0.08] transition-colors"
            title="Share">
            <Share2 className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>
      {copied && <p className="text-xs font-medium" style={{ color: brandColor }}>Link copied to clipboard!</p>}
    </div>
  );
}
